/**
 * NetworkGateway
 *
 * Single source of truth for network / tab-visibility state.
 * All polling, WebSocket channels, and periodic fetches register here
 * instead of each wiring their own visibilitychange + online/offline listeners.
 *
 * Usage:
 *   const unregister = NetworkGateway.register('my-feature', {
 *     onActivate:   () => startMyChannel(),   // tab visible + online
 *     onDeactivate: () => stopMyChannel(),    // tab hidden  OR offline
 *   });
 *   // call unregister() in cleanup
 *
 * For simple polling use the usePoll() React hook instead — it wraps this.
 */

type ConnectionHandlers = {
  onActivate: () => void;
  onDeactivate: () => void;
};

class NetworkGatewayClass {
  private connections = new Map<string, ConnectionHandlers>();
  private _isActive: boolean;

  constructor() {
    // Determine initial state (guard for non-browser environments).
    this._isActive =
      typeof document !== 'undefined' && typeof navigator !== 'undefined'
        ? !document.hidden && navigator.onLine
        : true;

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  /** True when the tab is visible AND the device has network connectivity. */
  get isActive(): boolean {
    return this._isActive;
  }

  /**
   * Register a connection / poll with the gateway.
   *
   * @param id       Stable string key — duplicate registrations replace the previous entry.
   * @param handlers { onActivate, onDeactivate }
   * @returns        Unregister function — call in useEffect cleanup.
   *
   * NOTE: onActivate is NOT called immediately on registration.  Call your
   * connect/start logic yourself on mount, then let the gateway handle pause/resume.
   */
  register(id: string, handlers: ConnectionHandlers): () => void {
    this.connections.set(id, handlers);
    return () => this.connections.delete(id);
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private setActive(next: boolean) {
    if (next === this._isActive) return;
    this._isActive = next;
    for (const { onActivate, onDeactivate } of this.connections.values()) {
      next ? onActivate() : onDeactivate();
    }
  }

  private handleVisibilityChange = () => {
    this.setActive(!document.hidden && navigator.onLine);
  };

  private handleOnline = () => {
    // Only activate if the tab is also visible.
    this.setActive(!document.hidden);
  };

  private handleOffline = () => {
    this.setActive(false);
  };
}

export const NetworkGateway = new NetworkGatewayClass();
